import { useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ddt from '@wix/da-ddt';
import { MeasuredCookieType, ScreenSizeContext } from '../../Measured';
import { hasCookie } from '../../Measured/redux/selectors';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
import { useShowMobileAd } from './useShowMobileAd';
import variables from '@wix/da-ds/src/styles/variables.scss';
var reservedScreenHeightForGenericLayout = [
    'meta-above-fold',
    'actionbar-height-mobile',
    'site-header-height-mobile',
]
    .map(function (prop) { return parseInt(variables[prop], 10); })
    .reduce(function (sum, x) { return sum + x; }, 0);
var reservedScreenHeightForAdLayout = ['site-header-height-mobile']
    .map(function (prop) { return parseInt(variables[prop], 10); })
    .reduce(function (sum, x) { return sum + x; }, 0);
var logger = ddt.createLogger('artstage');
function useIsFirstDeviationAfterSSR(deviation) {
    // this is a bit wacky, but we want to ensure we never recalc the stage for the first deviation after SSR
    // so here we check if this was the first deviation rendered in the component's lifecycle
    // combined with the duperbrowse check, should be Close Enough(tm)
    // we also take note if cookies existed, so important to have this hook run before the cookies get updated
    var isFirstDeviation = useRef(true);
    var firstRenderDeviationId = useRef(deviation.deviationId);
    var hadCookie = useSelector(function (state) {
        return hasCookie(state, MeasuredCookieType.SCREEN_WIDTH);
    });
    var firstDeviationHadCookie = useRef(hadCookie);
    if (isFirstDeviation.current &&
        firstRenderDeviationId.current !== deviation.deviationId) {
        isFirstDeviation.current = false;
    }
    return {
        isFirstDeviation: isFirstDeviation.current,
        hadCookie: firstDeviationHadCookie.current,
    };
}
function getReservedHeightForLayout(layout, screenWidth) {
    if (layout === void 0) { layout = 'generic'; }
    switch (layout) {
        case 'ads':
            // video ad does not have a set height. It has a 16:9 ratio
            // 16 for gap between image and ad and bottom of page
            return reservedScreenHeightForAdLayout + 16 + (screenWidth / 16) * 9;
        case 'generic':
        default:
            return reservedScreenHeightForGenericLayout;
    }
}
function calculateFixedStageSize(_a) {
    var screenWidth = _a.width, _b = _a.height, screenHeight = _b === void 0 ? 640 : _b, layout = _a.layout;
    var targetHeight = screenHeight - getReservedHeightForLayout(layout, screenWidth);
    var minHeight = layout === 'ads' ? 190 : 230;
    var stageHeight = Math.max(targetHeight, minHeight);
    return {
        containerSize: {
            width: screenWidth,
            height: stageHeight,
        },
        contentSize: {
            width: screenWidth,
            height: stageHeight,
        },
    };
}
export function useMobileStage(deviation) {
    var _a = useIsFirstDeviationAfterSSR(deviation), hadCookie = _a.hadCookie, isFirstDeviation = _a.isFirstDeviation;
    var _b = useContext(ScreenSizeContext), screenHeight = _b.height, screenWidth = _b.width;
    var deviationType = getDeviationType(deviation);
    var showAd = useShowMobileAd(deviation);
    var layout = showAd ? 'ads' : 'generic';
    // we do not want to recalculate when the screen height changes because that
    // happens a lot, eg. browser toolbars hiding, keyboard etc. Screen width
    // change will still trigger a recalc which is good enough for stuff like rotation
    var initialScreenHeight = useRef(screenHeight);
    initialScreenHeight.current = screenHeight; // do keep it up to date though
    var stageSize = useMemo(function () {
        // journals and commissions have a hardcoded height
        if ([DeviationTypes.JOURNAL, DeviationTypes.COMMISSION].includes(deviationType)) {
            return {
                containerSize: {
                    width: screenWidth,
                    height: 300,
                },
                contentSize: {
                    width: screenWidth,
                    height: 300,
                },
            };
        }
        // First deviation without cookie must be the default stage size
        if (!hadCookie && isFirstDeviation) {
            logger.log('No cookie');
            return calculateFixedStageSize({ width: screenWidth, layout: layout });
        }
        return calculateFixedStageSize({
            width: screenWidth,
            height: initialScreenHeight.current,
            layout: layout,
        });
    }, [hadCookie, isFirstDeviation, screenWidth, deviationType, layout]);
    return stageSize;
}
//# sourceMappingURL=useMobileStage.js.map